import React from 'react';
import { useDispatch } from 'react-redux';
import { baseDroneApi, useGetFleetDronesQuery } from '@src/core/features/drone/redux';
import { useGetUserDataQuery } from '@src/core/features';
import { ArcSocketContext, ChannelResponseEnum } from '@src/core/socket';
import { PAGINATION_DEFAULT_SIZE } from '@src/core/config';

export const usePaginatedDrones = (hubId: number | null = null) => {
  const dispatch = useDispatch();
  const [page, setPage] = React.useState(0);
  const { subscribe, unsubscribe } = React.useContext(ArcSocketContext);

  const { data: userData } = useGetUserDataQuery();
  const isAdmin = Boolean(userData?.roleStatus?.isAdmin);
  const scope = isAdmin ? 'ALL' : 'SELECTED_HUB';

  const { data, isLoading, isFetching, refetch } = useGetFleetDronesQuery({
    scope,
    hubId: hubId ?? undefined,
  });

  React.useEffect(() => {
    setPage(0);
  }, [hubId]);

  React.useEffect(() => {
    subscribe(ChannelResponseEnum.UPDATE_DRONES, () => {
      void refetch();
      // Fleet overview OTA banner reads flightdispatcher; refresh it on the same WS signal.
      dispatch(baseDroneApi.util.invalidateTags(['DroneFirmware']));
    });
    return () => unsubscribe(ChannelResponseEnum.UPDATE_DRONES);
  }, [dispatch, refetch, subscribe, unsubscribe]);

  const allDrones = data?.data ?? [];
  const totalCount = data?.totalCount ?? allDrones.length;
  const size = PAGINATION_DEFAULT_SIZE;

  const start = page * size;
  const drones = React.useMemo(
    () => allDrones.slice(start, start + size),
    [allDrones, start, size],
  );

  const handlePageChange = React.useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  return {
    allDrones,
    drones,
    isLoading,
    isFetching,
    refetch,
    pagination: {
      page,
      size,
      totalCount,
      onPageChange: handlePageChange,
    },
  };
};
